import { Avatar, Box, IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import Icons from '../../../components/icons';
import { AttendanceSource, StaffAttendanceView } from '../types';
import { StaffAttendanceStatus } from '../../../enums';
import pallet from '../../../layouts/pallet';
import { AbsentStatusWrapper, profileImgStyle } from '../styled';
import dayjs, { Dayjs } from 'dayjs';
import { defaultMappedRole } from '../../../config/schoolConfig';

const getStatus = (status: StaffAttendanceStatus) => {
  switch (status) {
    case StaffAttendanceStatus.LEAVE:
      return 'LEAVE';
    case StaffAttendanceStatus.ABSENT:
      return <AbsentStatusWrapper>ABSENT</AbsentStatusWrapper>;
    case StaffAttendanceStatus.HALF_DAY_PRESENT:
      return <span style={{ color: pallet.primary }}>HALF DAY</span>;
    case StaffAttendanceStatus.PRESENT:
      return <Box sx={{ color: pallet.green2 }}>PRESENT</Box>;
    default:
      return 'no record';
  }
};

const getMarkedByLabel = (source?: AttendanceSource) => {
  switch (source) {
    case AttendanceSource.ADMIN:
      return 'ADMIN';
    case AttendanceSource.TRACKER:
      return 'DEVICE';
    case AttendanceSource.CRON:
      return 'SYSTEM';
    case AttendanceSource.SELF:
      return 'STAFF';
    default:
      return '';
  }
};

export const getStaffAttendanceColumns = (
  onEdit: (params: StaffAttendanceView) => void,
  searchDate: Dayjs | null
): GridColDef[] => [
  {
    field: 'actions',
    headerName: 'Action',
    sortable: false,
    disableColumnMenu: true,
    minWidth: 120,
    flex: 1,
    renderCell: (params) => (
      <Box>
        <Tooltip title='Edit'>
          <IconButton onClick={() => onEdit(params.row as StaffAttendanceView)}>
            <Icons.EditBlackOutlined />
          </IconButton>
        </Tooltip>
      </Box>
    )
  },
  {
    field: 'displayRole',
    headerName: 'Role',
    // sortable: false,
    minWidth: 140,
    flex: 1,
    valueGetter: (_value: any, row: any) =>
      row?.displayRole ||
      defaultMappedRole[row?.role as unknown as string] ||
      row?.role ||
      '',
    renderCell: (params) => {
      return (
        params.row?.displayRole ||
        defaultMappedRole[params.row?.role as unknown as string] ||
        params.row?.role ||
        ''
      );
    }
  },
  {
    field: 'profile',
    headerName: 'Staff Photo',
    sortable: false,
    disableColumnMenu: true,
    disableExport: true,
    minWidth: 90,
    flex: 1,
    renderCell: (params) => {
      const src = params.row.profile;
      const staffName = params.row.name || '';
      if (!src) {
        const initials = staffName
          .split(' ')
          .filter(Boolean)
          .map((n: string) => n[0])
          .join('')
          .substring(0, 2)
          .toUpperCase();

        return (
          <Avatar
            variant='circular'
            sx={{
              width: 34,
              height: 34,
              fontSize: 14,
              bgcolor: pallet.primary
            }}
          >
            {initials}
          </Avatar>
        );
      }
      return <img src={src} alt={staffName} style={profileImgStyle} />;
    }
  },
  {
    field: 'memberCode',
    headerName: 'Member Code',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => params.row.memberCode || ''
  },
  {
    field: 'name',
    headerName: 'Staff Name',
    minWidth: 200,
    flex: 1
  },
  {
    field: 'attendanceDate',
    headerName: 'Date',
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      const dateStr = params.row.attendanceDate || searchDate;
      if (!dateStr) return '';
      return dayjs(dateStr).format('DD MMM, YYYY');
    }
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    minWidth: 100,
    flex: 1,
    renderCell: (params) =>
      getStatus(params.row.status as StaffAttendanceStatus)
  },

  {
    field: 'source',
    headerName: 'Marked By',
    sortable: false,
    minWidth: 100,
    flex: 1,
    valueGetter: (value: any, row: any) =>
      getMarkedByLabel(row?.source as AttendanceSource | undefined),
    renderCell: (params) =>
      getMarkedByLabel(params.row.source as AttendanceSource | undefined)
  },

  {
    field: 'shift',
    headerName: 'Shift',
    sortable: true,
    maxWidth: 180,
    flex: 1,
    valueGetter: (value: any, row: any) =>
      row?.shiftDetails?.startTime && row?.shiftDetails?.endTime
        ? `${row.shiftDetails.startTime} - ${row.shiftDetails.endTime}`
        : ''
  },
  {
    field: 'punchInTime',
    headerName: 'Punch In',
    sortable: false,
    minWidth: 120,
    flex: 1,
    renderCell: (params) => params.row.punchInTime || ''
  },

  {
    field: 'punchOutTime',
    headerName: 'Punch Out',
    sortable: false,
    minWidth: 120,
    flex: 1,
    renderCell: (params) => params.row.punchOutTime || ''
  },

  {
    field: 'markedAsLate',
    headerName: 'Marked As Late',
    sortable: false,
    minWidth: 90,
    flex: 1,
    renderCell: (params) => {
      const v = params.row.markedAsLate;
      if (v === true) return 'YES';
      if (v === false) return 'NO';
      return '';
    }
  },

  {
    field: 'remarks',
    headerName: 'Remarks',
    sortable: false,
    minWidth: 200,
    flex: 1,
    renderCell: (params) => params.row.remarks
  }
];
